import type { Company } from "@repo/app-types/entities/company";
import type { Project, ProjectUpdate } from "@repo/app-types/entities/project";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { Size } from "@repo/ui/helpers/helpers";
import type { FC } from "react";
import { AIContextSetting } from "./aiContextSetting.js";

interface OtherSettingsSectionProps {
  onSaveProject: (update: ProjectUpdate) => void;
  company: Company;
  project: Project;
  interviewLimitSettingRef: React.MutableRefObject<HTMLLabelElement | null>;
}

export const OtherSettingsSection: FC<OtherSettingsSectionProps> = ({
  onSaveProject,
  company,
  project,
  interviewLimitSettingRef,
}) => {
  return (
    <div className="mb-10 pb-6">
      <Label size={Size.LARGE}>Other settings</Label>
      <div className="mt-3">
        <AIContextSetting
          context={project.settings.prompt_context}
          onSave={(newContext) => {
            onSaveProject({
              id: project.id,
              settings: {
                ...project.settings,
                prompt_context: newContext || null,
              },
            });
          }}
        />
      </div>
      <label className="mt-2 block text-sm leading-6 font-semibold text-foreground">
        Clarifying questions limit
      </label>
      <div className="mt-2 w-52">
        <SimpleSelect
          options={maxClarifyingQuestionsOptions}
          value={project.settings.max_clarifying_questions_count ?? 2}
          onChange={(newLimit) => {
            onSaveProject({
              id: project.id,
              settings: {
                ...project.settings,
                max_clarifying_questions_count: newLimit,
              },
            });
          }}
        />
      </div>
      <div className="mt-2 text-sm text-muted-foreground">
        The maximum number of dynamic clarifying questions to ask based on a
        participant's response to each interview question. Dynamic clarifying
        questions can help elicit more detailed, insightful responses, and are
        only asked when a participant's response isn't detailed enough.
      </div>
      <label
        className="mt-2 block text-sm leading-6 font-semibold text-foreground"
        ref={interviewLimitSettingRef}
      >
        Monthly interview limit
      </label>
      <div className="mt-2 w-56">
        <SimpleSelect
          options={interviewThrottlingOptions}
          value={project.settings.max_interview_count}
          onChange={(newLimit) => {
            onSaveProject({
              id: project.id,
              settings: {
                ...project.settings,
                max_interview_count: newLimit,
              },
            });
          }}
        />
      </div>
      <div className="mt-2 text-sm text-muted-foreground">
        When the monthly interview limit is reached, participants that open an
        interview link will be blocked from starting an interview. For the best
        insights and participant experience, we recommend a high limit or none
        at all.
      </div>
    </div>
  );
};

const interviewThrottlingOptions: readonly SimpleSelectOption<number | null>[] =
  [
    { label: "No limit (recommended)", value: null },
    { label: "10 interviews per month", value: 10 },
    { label: "20 interviews per month", value: 20 },
    { label: "30 interviews per month", value: 30 },
    { label: "50 interviews per month", value: 50 },
    { label: "100 interviews per month", value: 100 },
    { label: "150 interviews per month", value: 150 },
    { label: "200 interviews per month", value: 200 },
  ];

const maxClarifyingQuestionsOptions: readonly SimpleSelectOption<number>[] = [
  { label: "No clarifying questions", value: 0 },
  { label: "1 per interview question", value: 1 },
  { label: "2 per interview question", value: 2 },
];
